import * as React from "react";
import { Link } from "react-router-dom";
import { Plus, ShieldCheck } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { useWalletStore } from "@/stores/wallet-store";
import {
  GOVERNANCE_CATEGORIES,
  POLICIES,
  POLICY_APPROVAL_ROUTE,
  POLICY_EVALUATIONS,
  POLICY_STATS,
  GOV_EVAL_STATS,
  type Policy,
} from "./oversight-data";
import { EmptyRow, Panel, SectionTitle, ToneBadge, statusToneFor } from "./oversight-ui";

const CATEGORY_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Categories" },
  ...GOVERNANCE_CATEGORIES.map((c) => ({ value: c.category, label: c.category })),
];
const STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

function PolicyTable({ policies }: { policies: Policy[] }) {
  return (
    <TableWrapper>
      <Table className="dense">
        <thead>
          <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
            <th className="px-4 py-2.5">ID</th>
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Wallets</th>
            <th className="px-4 py-2.5">Last Evaluated</th>
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.id} className="border-b border-line last:border-b-0">
              <td className="px-4 py-3">
                <Link to={`/governance/${p.id}`} className="font-mono text-[13px] font-semibold text-primary hover:underline">
                  {p.id}
                </Link>
              </td>
              <td className="px-4 py-3 text-[13px] font-medium text-ink">{p.name}</td>
              <td className="px-4 py-3">
                <ToneBadge tone={p.statusTone}>{p.statusLabel}</ToneBadge>
              </td>
              <td className="px-4 py-3 text-[13px] tabular-nums text-text">{p.wallets}</td>
              <td className="px-4 py-3 text-[13px] text-muted">{p.lastEvaluated}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}

export function GovernanceOverview() {
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [status, setStatus] = React.useState("");

  const filtered = React.useMemo(
    () =>
      POLICIES.filter((p) => {
        const q = query.toLowerCase();
        return (
          (!q || `${p.id} ${p.name} ${p.category}`.toLowerCase().includes(q)) &&
          (!category || p.category === category) &&
          (!status || p.status === status)
        );
      }),
    [query, category, status]
  );

  return (
    <div>
      <PageHeader
        title="Governance"
        subtitle="Policy definitions evaluated against every transfer intent before signing."
        actions={
          <Button
            size="sm"
            onClick={() =>
              toast({
                variant: "info",
                title: "Policy authoring is not built yet",
                description: "Draft POL-006 is the working example until the authoring module lands.",
              })
            }
          >
            <Plus className="size-4" aria-hidden /> Create Policy
          </Button>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {POLICY_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} tone={s.tone} />
        ))}
      </div>

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search policies by ID, name, category…" />
        <FilterSelect value={category} onChange={setCategory} options={CATEGORY_FILTERS} ariaLabel="Filter by category" />
        <FilterSelect value={status} onChange={setStatus} options={STATUS_FILTERS} ariaLabel="Filter by status" />
      </FilterBar>

      {filtered.length === 0 ? (
        <Panel>
          <div className="px-4 py-10 text-center text-sm text-muted">No policies match your filters.</div>
        </Panel>
      ) : (
        GOVERNANCE_CATEGORIES.filter((c) => filtered.some((p) => p.category === c.category)).map((c) => (
          <section key={c.category} className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <SectionTitle className="mb-0">{c.category}</SectionTitle>
              <span className="rounded-full bg-bg px-2 py-0.5 text-[11px] font-semibold text-muted">
                {filtered.filter((p) => p.category === c.category).length}
              </span>
            </div>
            <PolicyTable policies={filtered.filter((p) => p.category === c.category)} />
          </section>
        ))
      )}
    </div>
  );
}

const TYPE_LABEL: Record<string, string> = { hot: "Hot", cold: "Cold", warm: "Warm" };

export function PolicyDetail({ id }: { id: string }) {
  const wallets = useWalletStore((s) => s.wallets);
  const policy = POLICIES.find((p) => p.id === id);

  if (!policy) {
    return (
      <div>
        <Breadcrumb items={[{ label: "Governance", href: "/governance" }, { label: id }]} />
        <Panel>
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-semibold text-ink">Policy {id} was not found.</p>
            <p className="mt-1 text-xs text-muted">It may have been retired. Return to the policy overview.</p>
            <Link to="/governance" className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "mt-4")}>
              Back to Governance
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  const recent = POLICY_EVALUATIONS.filter((e) => e.policyId === policy.id).slice(0, 5);

  return (
    <div>
      <Breadcrumb items={[{ label: "Governance", href: "/governance" }, { label: policy.id }]} />
      <PageHeader
        title={policy.name}
        subtitle={policy.description}
        actions={<ToneBadge tone={policy.statusTone}>{policy.statusLabel}</ToneBadge>}
      />

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <Panel title={<span className="flex items-center gap-2"><ShieldCheck className="size-4 text-primary" aria-hidden />Policy Attributes</span>} bodyClassName="px-5 py-3">
            <dl className="divide-y divide-line">
              {[
                { k: "Policy ID", v: policy.id, mono: true },
                { k: "Type", v: policy.type },
                { k: "Status", v: policy.statusLabel },
                { k: "Created", v: policy.created },
                { k: "Last Modified", v: policy.modified },
              ].map((r) => (
                <div key={r.k} className="flex items-center justify-between gap-3 py-2.5 text-xs">
                  <dt className="text-muted">{r.k}</dt>
                  <dd className={cn("text-right font-semibold text-ink", r.mono && "font-mono")}>{r.v}</dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="Approval Route" bodyClassName="px-5 py-4">
            <p className="mb-3 text-xs text-muted">{POLICY_APPROVAL_ROUTE.label} — {POLICY_APPROVAL_ROUTE.needed} of {POLICY_APPROVAL_ROUTE.total} required.</p>
            <ul className="space-y-2.5">
              {POLICY_APPROVAL_ROUTE.approvers.map((a) => (
                <li key={a.name} className="flex items-center gap-2.5">
                  <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary-soft text-[11px] font-bold text-primary">{a.initials}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-ink">{a.name}</div>
                    <div className="truncate text-[11px] text-muted">{a.role}</div>
                  </div>
                  <ToneBadge tone={a.statusTone} dot={false}>{a.statusLabel}</ToneBadge>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <div>
          <Panel title="Conditions" bodyClassName="px-5 py-4">
            <ul className="space-y-2">
              {policy.conditions.map((c, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-bg px-3 py-2 text-[13px]">
                  <span className="font-medium text-ink">{c.field}</span>
                  <span className="rounded bg-surface px-1.5 py-0.5 font-mono text-[11px] font-bold text-primary">{c.operator}</span>
                  <span className="text-text">{c.value}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Affected Wallets" bodyClassName="p-0">
            {policy.walletIds.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted">No wallets are bound while this policy is a draft.</div>
            ) : (
              <TableWrapper className="rounded-none border-0">
                <Table className="dense">
                  <thead>
                    <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                      <th className="px-4 py-2.5">Wallet</th>
                      <th className="px-4 py-2.5">Network</th>
                      <th className="px-4 py-2.5">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policy.walletIds.map((w) => {
                      const wallet = wallets.find((x) => x.id === w);
                      return (
                        <tr key={w} className="border-b border-line last:border-b-0">
                          <td className="px-4 py-3">
                            <Link to={`/wallets/${w}`} className="font-mono text-[13px] font-semibold text-primary hover:underline">{w}</Link>
                          </td>
                          <td className="px-4 py-3 text-[13px] text-text">{wallet?.chain ?? "—"}</td>
                          <td className="px-4 py-3 text-[13px] text-text">{wallet ? (TYPE_LABEL[wallet.type] ?? wallet.type) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            )}
          </Panel>

          <Panel
            title="Recent Evaluations"
            actions={<Link to="/governance/evaluation" className="text-xs font-semibold text-primary hover:underline">View all</Link>}
            bodyClassName="p-0"
          >
            <TableWrapper className="rounded-none border-0">
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                    <th className="px-4 py-2.5">Eval ID</th>
                    <th className="px-4 py-2.5">Intent</th>
                    <th className="px-4 py-2.5">Result</th>
                    <th className="px-4 py-2.5">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <EmptyRow colSpan={4}>No evaluations recorded for this policy yet.</EmptyRow>
                  ) : (
                    recent.map((e) => (
                      <tr key={e.id} className="border-b border-line last:border-b-0">
                        <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{e.id}</td>
                        <td className="px-4 py-3 text-[13px] text-text">
                          {e.intentHref ? (
                            <Link to={e.intentHref} className="text-primary hover:underline">{e.intent}</Link>
                          ) : (
                            e.intent
                          )}
                        </td>
                        <td className="px-4 py-3"><ToneBadge tone={e.resultTone}>{e.result}</ToneBadge></td>
                        <td className="px-4 py-3 text-[13px] text-muted">{e.timestamp}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function EvaluationHistory() {
  const [result, setResult] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState(POLICY_EVALUATIONS[0].id);

  const filtered = React.useMemo(
    () =>
      POLICY_EVALUATIONS.filter((e) => {
        const q = query.toLowerCase();
        return (
          (!q || `${e.id} ${e.policyId} ${e.policyName} ${e.intent}`.toLowerCase().includes(q)) &&
          (!result || e.result === result)
        );
      }),
    [query, result]
  );

  const selectedEval = POLICY_EVALUATIONS.find((e) => e.id === selected) ?? filtered[0];

  return (
    <div>
      <Breadcrumb items={[{ label: "Governance", href: "/governance" }, { label: "Evaluation History" }]} />
      <PageHeader title="Policy Evaluation History" subtitle="Every policy decision, newest first, with its evidence receipt." />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {GOV_EVAL_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} tone={s.tone} />
        ))}
      </div>

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search evaluations by ID, policy, intent…" />
        <FilterSelect
          value={result}
          onChange={setResult}
          options={[
            { value: "", label: "All Results" },
            { value: "Passed", label: "Passed" },
            { value: "Blocked", label: "Blocked" },
            { value: "Held", label: "Held" },
            { value: "Requires Approval", label: "Requires Approval" },
          ]}
          ariaLabel="Filter by result"
        />
      </FilterBar>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <TableWrapper>
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Eval ID</th>
                <th className="px-4 py-2.5">Policy</th>
                <th className="px-4 py-2.5">Intent</th>
                <th className="px-4 py-2.5">Result</th>
                <th className="px-4 py-2.5">Actor</th>
                <th className="px-4 py-2.5">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <EmptyRow colSpan={6}>No evaluations match your filters.</EmptyRow>
              ) : (
                filtered.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setSelected(e.id)}
                    className={cn("cursor-pointer border-b border-line last:border-b-0", selected === e.id && "bg-primary-soft")}
                  >
                    <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{e.id}</td>
                    <td className="px-4 py-3 font-mono text-[13px] text-text">{e.policyId}</td>
                    <td className="px-4 py-3 text-[13px] text-text">{e.intent}</td>
                    <td className="px-4 py-3"><ToneBadge tone={e.resultTone}>{e.result}</ToneBadge></td>
                    <td className="px-4 py-3 text-[13px] text-muted">{e.actor}</td>
                    <td className="px-4 py-3 text-[13px] text-muted">{e.timestamp}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>

        {selectedEval && (
          <Panel title={`Receipt — ${selectedEval.id}`} bodyClassName="px-5 py-3">
            <dl className="divide-y divide-line">
              {[
                { k: "Evaluation", v: selectedEval.id, mono: true },
                { k: "Policy", v: `${selectedEval.policyId} — ${selectedEval.policyName}` },
                { k: "Intent", v: selectedEval.intent },
                { k: "Result", v: selectedEval.result },
                { k: "Actor", v: selectedEval.actor },
                { k: "Timestamp", v: selectedEval.timestamp },
              ].map((r) => (
                <div key={r.k} className="flex items-start justify-between gap-3 py-2.5 text-xs">
                  <dt className="shrink-0 text-muted">{r.k}</dt>
                  <dd className={cn("text-right font-semibold text-ink", r.mono && "font-mono")}>{r.v}</dd>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 py-2.5 text-xs">
                <dt className="text-muted">Evidence</dt>
                <dd>
                  {selectedEval.evidenceId ? (
                    <Link to={`/evidence/${selectedEval.evidenceId}`} className="font-semibold text-primary hover:underline">
                      {selectedEval.evidenceId}
                    </Link>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </dd>
              </div>
            </dl>
            <div className="border-t border-line pt-3">
              <ToneBadge tone={statusToneFor(selectedEval.result)}>{selectedEval.result}</ToneBadge>
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
